import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Identity } from "../../api/client";
import { readCsrfCookie } from "./csrfCookie";

/**
 * `checking` covers the one round trip on first load that asks whether the
 * session cookie (if any) is still valid — until it resolves, neither the
 * sign-in screen nor the dashboard is the right thing to show.
 */
export type AuthStatus = "checking" | "signed_out" | "signed_in";

export interface AuthState {
  status: AuthStatus;
  identity: Identity | null;
}

export interface LoginOutcome {
  ok: boolean;
  message?: string;
}

function describeLoginError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return "Too many attempts. Try again later.";
    return "Incorrect username or password.";
  }
  return "Could not reach the server.";
}

/**
 * Tracks the signed-in identity by asking the API, never by trusting
 * anything stored client-side: the session cookie is HttpOnly and the source
 * of truth is always /v1/auth/me or a login response
 * (CLAUDE.md: tenant identity comes from authenticated membership only).
 */
export function useAuth(): AuthState & {
  login: (username: string, password: string) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
} {
  const [state, setState] = useState<AuthState>({ status: "checking", identity: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const identity = await api.me();
        if (!cancelled) setState({ status: "signed_in", identity });
      } catch {
        if (!cancelled) setState({ status: "signed_out", identity: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<LoginOutcome> => {
    try {
      const identity = await api.login(username, password);
      setState({ status: "signed_in", identity });
      return { ok: true };
    } catch (error) {
      return { ok: false, message: describeLoginError(error) };
    }
  }, []);

  const logout = useCallback(async () => {
    const csrfToken = readCsrfCookie(document.cookie);
    try {
      await api.logout(csrfToken);
    } catch {
      // Logging out is best-effort client-side: even if the request fails,
      // there is nothing more useful to do than forget the local identity.
    }
    setState({ status: "signed_out", identity: null });
  }, []);

  return { ...state, login, logout };
}
